// Versão: 2.01
// Data..: 2026-09-24
// Autenticação via Amazon Cognito. Suporta troca de senha no primeiro acesso
// (NEW_PASSWORD_REQUIRED) e MFA por app autenticador (TOTP / SOFTWARE_TOKEN_MFA),
// incluindo o desafio no login e o gerenciamento (ativar/desativar) na conta.
// v2.01: MFA obrigatório — trata o desafio MFA_SETUP no login (configurar o app
// autenticador na hora, quando o usuário ainda não tem MFA ativo).

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  type CognitoUserSession,
  type IAuthenticationCallback,
} from 'amazon-cognito-identity-js';
import { config } from '../config.js';

const pool = new CognitoUserPool({
  UserPoolId: config.cognito.userPoolId,
  ClientId: config.cognito.clientId,
});

// Resultado do login: token pronto, desafio de nova senha, desafio de MFA (TOTP),
// ou obrigação de configurar o MFA na hora (MFA_SETUP, quando o MFA é obrigatório
// e o usuário ainda não ativou o app autenticador).
export type SignInResult =
  | { status: 'ok'; token: string }
  | { status: 'new_password_required'; setNewPassword: (newPassword: string) => Promise<SignInResult> }
  | { status: 'totp_required'; submitTotp: (code: string) => Promise<string> }
  | {
      status: 'totp_setup_required';
      // Segredo (base32) para montar o QR code / chave manual do app autenticador.
      secret: string;
      // Verifica o código do app, ativa o MFA e conclui o login retornando o token.
      confirmSetup: (code: string) => Promise<string>;
    };

// Faz login. Trata os desafios de primeiro acesso (nova senha), MFA por app (TOTP)
// e configuração obrigatória de MFA (MFA_SETUP).
export function signIn(email: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: pool });
    const details = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(details, buildAuthCallbacks(user, resolve, reject));
  });
}

// Monta os callbacks de authenticateUser/completeNewPasswordChallenge. É reutilizado
// para que, após definir a nova senha, o mesmo tratamento de MFA_SETUP/TOTP se aplique.
function buildAuthCallbacks(
  user: CognitoUser,
  resolve: (r: SignInResult) => void,
  reject: (e: unknown) => void,
): IAuthenticationCallback {
  return {
    onSuccess: (session: CognitoUserSession) => {
      resolve({ status: 'ok', token: session.getIdToken().getJwtToken() });
    },
    onFailure: (err) => reject(err),
    // Primeiro acesso: em vez de erro, conduz à definição da nova senha.
    newPasswordRequired: (userAttributes: Record<string, unknown>) => {
      delete userAttributes.email_verified;
      delete userAttributes.email;
      delete (userAttributes as Record<string, unknown>).phone_number_verified;
      resolve({
        status: 'new_password_required',
        // Após definir a senha o Cognito pode encadear MFA_SETUP/TOTP: reaproveita
        // os mesmos callbacks e devolve o próximo SignInResult para a UI.
        setNewPassword: (newPassword: string) =>
          new Promise<SignInResult>((res, rej) => {
            user.completeNewPasswordChallenge(newPassword, userAttributes, buildAuthCallbacks(user, res, rej));
          }),
      });
    },
    // MFA por app autenticador (usuário já tem TOTP): pede o código de 6 dígitos.
    totpRequired: () => {
      resolve({
        status: 'totp_required',
        submitTotp: (code: string) =>
          new Promise<string>((res, rej) => {
            user.sendMFACode(
              code,
              {
                onSuccess: (session: CognitoUserSession) => res(session.getIdToken().getJwtToken()),
                onFailure: (e) => rej(e),
              },
              'SOFTWARE_TOKEN_MFA',
            );
          }),
      });
    },
    // MFA obrigatório e usuário ainda sem app autenticador: associa um novo segredo
    // e devolve à UI o segredo (para o QR code) + a função de confirmação.
    mfaSetup: () => {
      user.associateSoftwareToken({
        associateSecretCode: (secret: string) => {
          resolve({
            status: 'totp_setup_required',
            secret,
            confirmSetup: (code: string) =>
              new Promise<string>((res, rej) => {
                user.verifySoftwareToken(code, 'App autenticador', {
                  onSuccess: (session: CognitoUserSession) => {
                    // Marca o TOTP como preferência (silencioso: o login já está concluído).
                    user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, () => {});
                    res(session.getIdToken().getJwtToken());
                  },
                  onFailure: (e) => rej(e),
                });
              }),
          });
        },
        onFailure: (e) => reject(e),
      });
    },
  };
}

export function signOut(): void {
  pool.getCurrentUser()?.signOut();
}

// Recupera o token da sessão atual, se válida.
export function getToken(): Promise<string | null> {
  return new Promise((resolve) => {
    const user = pool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

// ---- Gerenciamento de MFA (na conta do usuário logado) ----

// Garante uma sessão válida no CognitoUser atual (necessária para operações de MFA).
function currentUserWithSession(): Promise<CognitoUser> {
  return new Promise((resolve, reject) => {
    const user = pool.getCurrentUser();
    if (!user) return reject(new Error('Sessão não encontrada.'));
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return reject(new Error('Sessão inválida.'));
      resolve(user);
    });
  });
}

// Indica se o MFA por app já está ativo para o usuário logado.
export function getMfaEnabled(): Promise<boolean> {
  return new Promise((resolve, reject) => {
    currentUserWithSession()
      .then((user) => {
        user.getUserData((err, data) => {
          if (err || !data) return resolve(false);
          const list = data.UserMFASettingList ?? [];
          resolve(list.includes('SOFTWARE_TOKEN_MFA'));
        }, { bypassCache: true });
      })
      .catch(reject);
  });
}

// Inicia a associação de um app autenticador: retorna o segredo (para o QR code).
export function startTotpSetup(): Promise<string> {
  return new Promise((resolve, reject) => {
    currentUserWithSession()
      .then((user) => {
        user.associateSoftwareToken({
          associateSecretCode: (secret: string) => resolve(secret),
          onFailure: (e) => reject(e),
        });
      })
      .catch(reject);
  });
}

// Confirma o código do app e ativa o MFA por app como preferência do usuário.
export function verifyTotp(code: string, deviceName = 'App autenticador'): Promise<void> {
  return new Promise((resolve, reject) => {
    currentUserWithSession()
      .then((user) => {
        user.verifySoftwareToken(code, deviceName, {
          onSuccess: () => {
            user.setUserMfaPreference(
              null,
              { PreferredMfa: true, Enabled: true },
              (err) => (err ? reject(err) : resolve()),
            );
          },
          onFailure: (e) => reject(e),
        });
      })
      .catch(reject);
  });
}

// Desativa o MFA por app do usuário logado.
export function disableTotp(): Promise<void> {
  return new Promise((resolve, reject) => {
    currentUserWithSession()
      .then((user) => {
        user.setUserMfaPreference(
          null,
          { PreferredMfa: false, Enabled: false },
          (err) => (err ? reject(err) : resolve()),
        );
      })
      .catch(reject);
  });
}

// URL otpauth:// para gerar o QR code do app autenticador.
export function totpUri(secret: string, email: string): string {
  const issuer = encodeURIComponent('Makweb Portal');
  const label = encodeURIComponent(email);
  return `otpauth://totp/${issuer}:${label}?secret=${secret}&issuer=${issuer}`;
}
